import { useState, useCallback } from "react";
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    getMultiFactorResolver,
    RecaptchaVerifier,
    getAuth,
} from "firebase/auth";
import { firebaseAuthApp } from "../../firebase/auth";


export function useLoginWithMFA() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(null);

    // 保存 resolver / verificationId
    const [resolver, setResolver] = useState(null);
    const [verificationId, setVerificationId] = useState(null);

    const handleLogin = useCallback(async (loginFn) => {
        setLoading(true);
        setError(null);
        setMfaRequired(false);
        setPhoneNumber(null);

        try {
            const userCredential = await loginFn();
            setUser(userCredential.user);
        } catch (err) {
            if (err.code === "auth/multi-factor-auth-required") {
                console.log("⚠️ 需要 MFA 验证");
                const r = getMultiFactorResolver(getAuth(firebaseAuthApp()), err);
                setResolver(r);

                if (!(window).recaptchaVerifier) {
                    (window).recaptchaVerifier = new RecaptchaVerifier(
                        getAuth(firebaseAuthApp()),
                        "recaptcha-container",
                        { size: "invisible" },
                    );
                }

                console.log(r);

                const hint = r.hints.find((h) => h.factorId === "phone");
                setPhoneNumber(hint.phoneNumber);

                let recaptchaVerifier = window.recaptchaVerifier;
                await recaptchaVerifier.verify();
                const phoneAuthProvider = new PhoneAuthProvider(getAuth(firebaseAuthApp()));
                const vId = await phoneAuthProvider.verifyPhoneNumber(
                    { multiFactorHint: hint, session: r.session },
                    recaptchaVerifier
                );

                setVerificationId(vId);
                setMfaRequired(true);
            } else {
                setError(err.message || "登录失败");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const loginWithEmail = useCallback(
        async (email, password) => {
            return handleLogin(() =>
                signInWithEmailAndPassword(getAuth(firebaseAuthApp()), email, password)
            );
        },
        [handleLogin]
    );

    const loginWithPopup = useCallback(
        async (provider) => {
            return handleLogin(() => signInWithPopup(getAuth(firebaseAuthApp()), provider));
        },
        [handleLogin]
    );

    const verifyCode = useCallback(
        async (code) => {
            if (!resolver || !verificationId) {
                setError("缺少 MFA 验证信息");
                return;
            }
            setLoading(true);
            setError(null);

            try {
                const credential = PhoneAuthProvider.credential(verificationId, code);
                const assertion = PhoneMultiFactorGenerator.assertion(credential);
                const userCredential = await resolver.resolveSignIn(assertion);

                setUser(userCredential.user);
                setMfaRequired(false);
                setPhoneNumber(null);
                setResolver(null);
                setVerificationId(null);
            } catch (err) {
                setError(err.message || "验证码错误");
            } finally {
                setLoading(false);
            }
        },
        [resolver, verificationId]
    );

    // 重新发送验证码
    const resendCode = useCallback(async () => {
        if (!resolver) {
            setError("缺少 MFA 验证信息");
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const phoneHint = resolver.hints.find((h) => h.factorId === "phone");

            let recaptchaVerifier = (window).recaptchaVerifier;
            await recaptchaVerifier.verify();

            const phoneAuthProvider = new PhoneAuthProvider(getAuth(firebaseAuthApp()));
            const vId = await phoneAuthProvider.verifyPhoneNumber(
                { multiFactorHint: phoneHint, session: resolver.session },
                recaptchaVerifier
            );
            setVerificationId(vId);
        } catch (err) {
            setError(err.message || "重新发送验证码失败");
        } finally {
            setLoading(false);
        }
    }, [resolver]);


    return {
        user,
        loading,
        error,
        mfaRequired,
        phoneNumber,
        loginWithEmail,
        loginWithPopup,
        verifyCode,
        resendCode,
    };
}
